import React, { useState, useEffect } from 'react';

/**
 * Navbar Component
 * Floating navigation bar with hide-on-scroll-down behavior
 * Maintains dropdown functionality with PS 155 design system
 */
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openNestedDropdown, setOpenNestedDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.pageYOffset;

      // Add background when scrolled past hero
      setScrolled(currentScrollPos > 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest('nav')) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const navItems = [
    {
      label: 'Our School',
      items: [
        { label: 'About Us', href: '/about' },
        { label: 'Principal Message', href: '/principal-message' },
        { label: 'Core Values', href: '/core-values' },
        { label: 'Curriculum', href: '/curriculum' },
        { label: 'Staff Directory', href: '/staff-directory' },
        { label: 'Welcome Video', href: 'https://www.youtube.com/watch?v=S7FOgoxQNns', external: true },
      ]
    },
    {
      label: 'Programs',
      items: [
        { label: 'School-Wide Initiatives', href: '/initiatives' },
        { label: 'Our Partnerships', href: '/partnerships' },
        {
          label: 'During School Program',
          items: [
            { label: 'Our CBO - Wediko', href: '/wediko' },
            { label: 'Dual Language Programming', href: '/dual-language' },
            { label: 'NYCFC Soccer', href: '/nycfc-soccer' },
            { label: 'Chess', href: '/chess' },
            { label: 'Arts for ALL', href: '/arts-for-all' },
            { label: 'Asphalt Green Swimming', href: '/asphalt-green' },
            { label: 'Choose Creativity', href: '/choose-creativity' },
          ]
        },
        {
          label: 'After-School Program',
          items: [
            { label: 'Wediko Afterschool', href: 'https://www.thehome.org/programs/wediko-new-york-city/', external: true },
            { label: 'Creative Art Works', href: 'https://www.creativeartworks.org/', external: true },
          ]
        },
      ]
    },
    {
      label: 'Student Resources',
      items: [
        { label: 'Google Classroom', href: 'https://edu.google.com/intl/ALL_us/workspace-for-education/products/classroom/', external: true },
        { label: 'I-Ready', href: 'https://login.i-ready.com/', external: true },
        { label: 'IXL', href: 'https://www.ixl.com/', external: true },
        { label: 'Epic! Books', href: 'https://www.getepic.com/sign-in', external: true },
      ]
    },
    {
      label: 'Families',
      items: [
        { label: 'Calendar', href: '/calendar' },
        { label: 'School Leadership Team (SLT)', href: '/school-leadership-team' },
        { label: 'Parent Handbook (English)', href: 'https://docs.google.com/document/d/1Wmud2491PhkgT8rTGPUBJ2-z9j10WK81sWM0sbwZSmQ/edit?tab=t.0', external: true },
        { label: 'Parent Handbook (Spanish)', href: 'https://docs.google.com/document/d/1KRUwYQccGSV9hL4bLfuROaaR5gMqzgZgGqlJII15WkQ/edit?tab=t.0', external: true },
        { label: 'Registration Process', href: 'https://www.schools.nyc.gov/enrollment/enrollment-help/new-students', external: true },
        { label: 'School Parent Compact', href: 'https://4.files.edl.io/7e41/12/16/24/001233-ad0f0c46-05ae-4911-a992-750152c46010.pdf', external: true },
        { label: 'Family Engagement', href: 'https://docs.google.com/forms/d/17dyGv3_KNmBtn4MkulFwha-RbLfUwmmPBVxkkeUB6nI/edit', external: true },
        { label: 'Our CEP', href: 'https://4.files.edl.io/9ed7/03/06/25/120031-df350740-6374-4f75-be0d-93cb1669272f.pdf', external: true },
      ]
    },
    {
      label: 'Contact Us',
      items: [
        { label: 'Contact Information', href: '/contact' },
        { label: 'Parent Concern Form', href: 'https://docs.google.com/forms/d/1-8nZHX5iWPBLyvL6RfrNTEJ99opslucrOcA9GxdOKBQ/edit', external: true },
        { label: 'School Tour/Question Request', href: 'mailto:Mstevenson@ps155.org' },
      ]
    },
  ];

  const handleMouseEnter = (index) => {
    setOpenDropdown(index);
  };

  const handleMouseLeave = () => {
    setOpenDropdown(null);
  };

  const handleSkipToContent = (e) => {
    e.preventDefault();
    document.getElementById('main-content')?.focus();
  };

  return (
    <>
      <a
        href="#main-content"
        onClick={handleSkipToContent}
        className="sr-only focus:not-sr-only focus:absolute focus:z-[60] focus:top-4 focus:left-4
                   focus:px-4 focus:py-2 focus:bg-school-gold focus:text-school-blue focus:rounded-lg
                   focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-school-gold"
      >
        Skip to content
      </a>
    <nav
      className="fixed top-4 left-1/2 transform -translate-x-1/2 w-11/12 md:w-[90%] lg:w-4/5
                 transition-all duration-300 z-50"
      aria-label="Main navigation"
    >
      <div className="relative flex items-center">
        {/* Logo - larger and vertically centered */}
        <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10">
          <a
            href="/"
            className="block focus:outline-none focus:ring-2 focus:ring-school-gold rounded-full"
          >
            <img
              src="/assets/images/school_logo.png"
              alt="P.S. 155 William Paca School"
              className="h-20 w-20 sm:h-24 sm:w-24 md:h-28 md:w-28 object-contain drop-shadow-lg"
            />
          </a>
        </div>

        {/* Navbar background - starts from middle of logo */}
        <div
          className={`rounded-2xl px-4 sm:px-6 py-3 sm:py-4 pl-16 sm:pl-20 ml-10 sm:ml-12 md:ml-14 flex-1 transition-all duration-300 ${
            scrolled
              ? 'bg-white shadow-md border border-neutral-200'
              : 'bg-school-blue-dark shadow-md'
          }`}
        >
          <div className="flex items-center justify-between">
          {/* Spacer for mobile to push hamburger to right */}
          <div className="flex-1 lg:hidden"></div>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center space-x-2 flex-1 justify-center">
            {navItems.map((item, index) => (
              <div
                key={index}
                className="relative"
                onMouseEnter={() => item.items && handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
                onFocus={() => item.items && handleMouseEnter(index)}
                onBlur={(e) => {
                  if (item.items && !e.currentTarget.contains(e.relatedTarget)) {
                    handleMouseLeave();
                  }
                }}
              >
                {item.items ? (
                  // Dropdown trigger
                  <button
                    className={`flex items-center gap-1 px-4 py-2 font-medium text-sm
                             transition-colors duration-150 rounded-lg focus:outline-none
                             focus:ring-2 focus:ring-school-gold ${
                      scrolled
                        ? 'text-neutral-700 hover:text-school-blue hover:bg-blue-50'
                        : 'text-white hover:text-school-gold-on-blue hover:bg-white/10'
                    }`}
                    aria-expanded={openDropdown === index}
                    aria-haspopup="true"
                  >
                    {item.label}
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 ${
                        openDropdown === index ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                ) : (
                  // Single link
                  <a
                    href={item.href}
                    aria-current={currentPath === item.href ? 'page' : undefined}
                    className={`flex items-center gap-1 px-4 py-2 font-medium text-sm
                             transition-colors duration-150 rounded-lg focus:outline-none
                             focus:ring-2 focus:ring-school-gold ${
                      scrolled
                        ? 'text-neutral-700 hover:text-school-blue hover:bg-blue-50'
                        : 'text-white hover:text-school-gold-on-blue hover:bg-white/10'
                    }`}
                  >
                    {item.label}
                  </a>
                )}

                {/* Dropdown Menu */}
                {item.items && openDropdown === index && (
                  <div className="absolute top-full left-0 pt-2">
                    <div
                      className="w-64 bg-white rounded-xl shadow-lg py-2 border border-neutral-200
                               animate-fadeIn"
                    >
                      {item.items.map((subItem, subIndex) => (
                        <div
                          key={subIndex}
                          className="relative"
                          onMouseEnter={() => subItem.items && setOpenNestedDropdown(subIndex)}
                          onMouseLeave={() => subItem.items && setOpenNestedDropdown(null)}
                        >
                          {subItem.items ? (
                            // Nested dropdown trigger
                            <button
                              className="w-full text-left px-4 py-2.5 text-sm text-neutral-700 hover:bg-blue-50
                                       hover:text-school-blue transition-colors duration-150
                                       focus:outline-none focus:bg-blue-50 focus:text-school-blue flex items-center justify-between"
                              aria-expanded={openNestedDropdown === subIndex}
                              aria-haspopup="true"
                            >
                              <span>{subItem.label}</span>
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth="2"
                                aria-hidden="true"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          ) : (
                            // Regular link
                            <a
                              href={subItem.href}
                              target={subItem.external ? '_blank' : undefined}
                              rel={subItem.external ? 'noopener noreferrer' : undefined}
                              className="block px-4 py-2.5 text-sm text-neutral-700 hover:bg-blue-50
                                       hover:text-school-blue transition-colors duration-150
                                       focus:outline-none focus:bg-blue-50 focus:text-school-blue"
                            >
                              {subItem.label}
                              {subItem.external && (
                                <svg
                                  className="w-3 h-3 inline-block ml-1 opacity-60"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                  />
                                </svg>
                              )}
                            </a>
                          )}

                          {/* Nested Dropdown Menu */}
                          {subItem.items && openNestedDropdown === subIndex && (
                            <div className="absolute left-full top-0 ml-1">
                              <div
                                className="w-64 bg-white rounded-xl shadow-lg py-2 border border-neutral-200
                                         animate-fadeIn"
                              >
                                {subItem.items.map((nestedItem, nestedIndex) => (
                                  <a
                                    key={nestedIndex}
                                    href={nestedItem.href}
                                    target={nestedItem.external ? '_blank' : undefined}
                                    rel={nestedItem.external ? 'noopener noreferrer' : undefined}
                                    className="block px-4 py-2.5 text-sm text-neutral-700 hover:bg-blue-50
                                             hover:text-school-blue transition-colors duration-150
                                             focus:outline-none focus:bg-blue-50 focus:text-school-blue"
                                  >
                                    {nestedItem.label}
                                    {nestedItem.external && (
                                      <svg
                                        className="w-3 h-3 inline-block ml-1 opacity-60"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                        />
                                      </svg>
                                    )}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA Button - Right aligned */}
          <div className="hidden lg:flex items-center flex-shrink-0">
            <a
              href="/contact"
              aria-current={currentPath === '/contact' ? 'page' : undefined}
              className="px-5 py-2.5 bg-school-gold text-school-blue-dark rounded-lg font-semibold text-sm
                       hover:bg-yellow-500 transition-all duration-150 hover:scale-105
                       focus:outline-none focus:ring-2 focus:ring-school-gold shadow-sm"
            >
              Get in Touch
            </a>
          </div>

          {/* Language selector mount - visible at every width, outside the mobile drawer and desktop-only wrapper */}
          <span id="site-language" className="inline-flex items-center ml-3 mr-1 flex-shrink-0"></span>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg focus:outline-none focus:ring-2
                     focus:ring-school-gold transition-colors duration-150 ${
              scrolled
                ? 'text-neutral-700 hover:bg-blue-50'
                : 'text-white hover:bg-white/10'
            }`}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? (
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-x-0 top-0 z-40 mt-36 mx-4 animate-slideDown">
          <div className="bg-white rounded-2xl shadow-2xl border border-neutral-200 max-h-[calc(100vh-10rem)] overflow-y-auto">
            <div className="p-6 space-y-4">
              {navItems.map((item, index) => (
                <div key={index} className="border-b border-neutral-100 last:border-0 pb-4 last:pb-0">
                  {item.items ? (
                    <div>
                      <button
                        onClick={() => setOpenDropdown(openDropdown === index ? null : index)}
                        className="w-full flex items-center justify-between py-2 font-medium text-neutral-700 hover:text-school-blue transition-colors"
                        aria-expanded={openDropdown === index}
                        aria-haspopup="true"
                      >
                        <span>{item.label}</span>
                        <svg
                          className={`w-5 h-5 transition-transform duration-200 ${
                            openDropdown === index ? 'rotate-180' : ''
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden="true"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {openDropdown === index && (
                        <div className="mt-2 ml-4 space-y-2 animate-fadeIn">
                          {item.items.map((subItem, subIndex) => (
                            <div key={subIndex}>
                              {subItem.items ? (
                                // Nested dropdown in mobile
                                <div>
                                  <button
                                    onClick={() => setOpenNestedDropdown(openNestedDropdown === subIndex ? null : subIndex)}
                                    className="w-full text-left py-2 px-3 text-sm text-neutral-600 hover:text-school-blue hover:bg-blue-50 rounded-lg transition-colors flex items-center justify-between"
                                    aria-expanded={openNestedDropdown === subIndex}
                                    aria-haspopup="true"
                                  >
                                    <span>{subItem.label}</span>
                                    <svg
                                      className={`w-4 h-4 transition-transform duration-200 ${
                                        openNestedDropdown === subIndex ? 'rotate-180' : ''
                                      }`}
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      aria-hidden="true"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                  {openNestedDropdown === subIndex && (
                                    <div className="mt-2 ml-4 space-y-2 animate-fadeIn">
                                      {subItem.items.map((nestedItem, nestedIndex) => (
                                        <a
                                          key={nestedIndex}
                                          href={nestedItem.href}
                                          target={nestedItem.external ? '_blank' : undefined}
                                          rel={nestedItem.external ? 'noopener noreferrer' : undefined}
                                          onClick={() => setMobileMenuOpen(false)}
                                          className="block py-2 px-3 text-sm text-neutral-600 hover:text-school-blue hover:bg-blue-50 rounded-lg transition-colors"
                                        >
                                          {nestedItem.label}
                                          {nestedItem.external && (
                                            <svg
                                              className="w-3 h-3 inline-block ml-1 opacity-60"
                                              fill="none"
                                              viewBox="0 0 24 24"
                                              stroke="currentColor"
                                              strokeWidth="2"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                              />
                                            </svg>
                                          )}
                                        </a>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                // Regular link in mobile
                                <a
                                  href={subItem.href}
                                  target={subItem.external ? '_blank' : undefined}
                                  rel={subItem.external ? 'noopener noreferrer' : undefined}
                                  onClick={() => setMobileMenuOpen(false)}
                                  className="block py-2 px-3 text-sm text-neutral-600 hover:text-school-blue hover:bg-blue-50 rounded-lg transition-colors"
                                >
                                  {subItem.label}
                                  {subItem.external && (
                                    <svg
                                      className="w-3 h-3 inline-block ml-1 opacity-60"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                      />
                                    </svg>
                                  )}
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <a
                      href={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      aria-current={currentPath === item.href ? 'page' : undefined}
                      className="block py-2 font-medium text-neutral-700 hover:text-school-blue transition-colors"
                    >
                      {item.label}
                    </a>
                  )}
                </div>
              ))}

              {/* Mobile CTA Button */}
              <div className="pt-4">
                <a
                  href="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-current={currentPath === '/contact' ? 'page' : undefined}
                  className="block w-full px-5 py-3 bg-school-gold text-school-blue-dark text-center rounded-lg font-semibold hover:bg-yellow-500 transition-all duration-150 shadow-sm"
                >
                  Get in Touch
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Animation styles */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 150ms ease-out;
        }
        .animate-slideDown {
          animation: slideDown 200ms ease-out;
        }
      `}</style>
    </nav>
    </>
  );
};

export default Navbar;
